/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {getOmegaEvent} from '@exc/events';
import {history} from '@exc/router';
import {I18nShell} from '@exc/shell';
import Metrics from '@exc/metrics';
import PropTypes from 'prop-types';
import React from 'react';
import urlManager, {
  getPathWithoutTenant,
  getQueryValue
} from './utils/URLManager';

const deferredComponentsPromise = import('./js-deferred');

export function getSelectedWorkspace(ws) {
  let {hash, pathname, search} = history.location;
  const {origin} = window.location;
  pathname = getPathWithoutTenant(pathname);
  const {searchParams} = new URL(`${origin}${search}`);
  let extendedPath = `${pathname}${search}${hash}`;

  return getWorkspaces(ws).find(w => {
    if (/^http/.test(w.url)) {
      return false;
    }
    const selected = w.selectOn && w.selectOn.find(s => s.test(extendedPath));
    if (selected) {
      return selected;
    }
    let url = w.url.endsWith('/') ? w.url : `${w.url}/`;
    url = new URL(`${origin}${w.url}`);
    const {hash: wsHash, pathname: wsPath, searchParams: wsSearchParams} = url;
    if (!(new RegExp(`^${wsPath}?`).test(pathname))) {
      return false;
    }
    const params = Array.from(wsSearchParams.keys());
    return hash === wsHash && params.every(key => searchParams.get(key) === wsSearchParams.get(key));
  });
}

/**
 * This function generates a list of workspaces, using BFS to identify menu
 * workspaces.
 * @param {Object[]} workspaces Array of workspaces.
 * @returns {Object[]} Array of workspaces including menu workspaces.
 */
function getWorkspaces(workspaces) {
  let queue = [];
  let menus = [];
  workspaces.forEach(w => {
    queue.push(w);
    while (queue.length) {
      let ws = queue.shift();
      menus.push(ws);
      ws.menu && ws.menu.forEach(m => queue.push(m));
    }
  });

  menus.sort((a, b) => {
    return a.url.split('/').length -  b.url.split('/').length;
  });

  menus.reverse();
  return menus;
}

export default class ShellWrapper extends React.Component {
  constructor(props) {
    super(props);
    this.metrics = new Metrics('exc.core.Shell');
  }

  onEditProfileClick() {
    const {shellResponse} = this.props;
    window.open(shellResponse.profileComponent.editProfileLink);
  }

  onWorkspaceChange = (w) => {
    if (/^https?:/.test(w.url)) {
      if (w.newtab) {
        return window.open(w.url, '_blank');
      }
      return window.location.assign(w.url);
    }
    urlManager.updatePath(w.url);
  }

  onSolutionClick(e, href) {
    const matches = this.matchesEnv(href, this.props.environment);
    let solutionName = e.target && e.target.innerText;

    // If the name is valid, track the link click type.
    if (solutionName) {
      solutionName = solutionName.replace(' ', '_');
      this.metrics.event('SolutionSwitcher.Load', {solutionName, source: matches ? 'Internal' : 'External'});
      this.metrics.analytics.trackEvent(getOmegaEvent({
        element: solutionName,
        event: e,
        widget: {name: 'solution switcher', type: 'popover'}
      }));
    }

    // Admin and Exchange are special cases under Experience Platform. They
    // should always open in a new tab.
    if (solutionName === 'Admin' || solutionName === 'Exchange') {
      e.preventDefault();
      window.open(href, '_blank');
      return;
    }
    // Match e.target.href against experience.adobe.com regex. If it's not,
    // navigate to the full href
    if (!matches) {
      window.location.href = href;
      return;
    }

    // If the solution is on experience.adobe.com, prevent the link action and
    // trigger the solution's route.
    e.preventDefault();
    let path = matches[2];

    // FIXME(rrp): This is hack because it is last second and I'm scared to change the router.
    if (path === '/target')  {
      path = '/target/activities';
    }

    urlManager.updatePath(path);
  }

  onHeroClick(e) {
    if (e.type === 'click' || e.key === 'Enter') {
      const {basePath, hero, noSolution} = this.props;

      if (noSolution) {
        urlManager.updatePath('/home');
        return;
      }

      this.metrics.analytics.trackEvent(getOmegaEvent({
        attributes: {icon: hero.icon, link: this.props.customHeroClick ? 'custom' : basePath},
        element: e.target && e.target.innerText,
        event: e,
        widget: {name: 'hero', type: 'button'}
      }));

      if (this.props.customHeroClick) {
        this.props.customHeroClick();
        return;
      }
      urlManager.updatePath(hero.path || basePath);
    }
  }

  matchesEnv(url, env) {
    if (env === 'dev') {
      return url.match(/localhost\.corp\.adobe\.com:[0-9]{4}(\/#)?(\/.*)/);
    }
    return url.match(/experience(?:|-qa|-stage)\.adobe\.com(\/#)?(\/.*)/);
  }

  onViewAllNotificationsClickHandler(activeTab = '') {
    urlManager.updatePath(`/home/notifications/${activeTab}`);
  }

  render() {
    const props = this.props;
    const {
      digitalData,
      hero,
      locale,
      selectedTenantId,
      shellResponse
    } = props;
    const profile = props.profile || {};
    const router = React.Children.only(props.children);
    const selectedWorkspace = getSelectedWorkspace(props.workspaces);

    const helpCenterConfig = {
      canAuthorize: props.environment === 'prod',
      customButtons: props.customButtons && props.customButtons.helpCenter,
      digitalData,
      feedbackAttachmentsEnabled: true,
      imsPayload: {
        accessToken: props.accessToken,
        displayName: profile.displayName,
        email: profile.email,
        firstName: profile.first_name,
        lastName: profile.last_name,
        userId: profile.userId
      },
      onCustomButtonClick: props.onCustomButtonClick,
      ...props.helpCenter,
      solutions: shellResponse.solutions,
      solutionTitle: hero.title
    };
    return (
      <I18nShell
        appId={props.appId}
        clientId={props.clientId}
        coachMark={props.coachMark}
        currentActiveTenantId={selectedTenantId}
        customButtons={props.customButtons}
        customEnvLabel={props.customEnvLabel}
        customProfileButtons={props.customButtons && props.customButtons.userProfile}
        customSearch={props.customSearch}
        defaultComponent={props.defaultComponent}
        deferredComponentsPromise={deferredComponentsPromise}
        environment={props.environment}
        feedback={props.feedback}
        getQueryValue={getQueryValue}
        helpCenterConfig={helpCenterConfig}
        imsOrgs={props.imsOrgs}
        landingpage={shellResponse.landingpage}
        locale={locale}
        modal={props.modal}
        nps={props.nps}
        onCustomButtonClick={props.onCustomButtonClick}
        onCustomProfileButtonClick={props.onCustomButtonClick}
        onImsOrgChange={props.onImsOrgChange}
        onPulseViewAllClick={this.onViewAllNotificationsClickHandler.bind(this)}
        onSignOutClick={props.onSignOut}
        onSolutionClick={this.onHeroClick.bind(this)}
        onSolutionSwitcherClick={this.onSolutionClick.bind(this)}
        onUserProfileClick={this.onEditProfileClick.bind(this)}
        onWorkspaceChange={this.onWorkspaceChange}
        orgToSubOrgsMap={props.orgToSubOrgsMap}
        profile={profile}
        selectedImsOrg={props.selectedImsOrg}
        selectedSubOrg={props.selectedSubOrg}
        selectedWorkspaceId={selectedWorkspace && selectedWorkspace.uid}
        serviceEnvironment={props.serviceEnvironment}
        services={shellResponse.services}
        settingsService={props.settingsService}
        solutionIcon={hero.icon}
        solutionShortTitle={hero.shortTitle}
        solutionTitle={hero.title}
        solutions={shellResponse.solutions}
        supportsViewportTakeover={props.supportsViewportTakeover}
        translationService={props.translationService}
        userToken={props.accessToken}
        viewportTakeover={props.viewportTakeover}
        workspaces={props.workspaces}
      >
        <React.Suspense fallback={props.defaultComponent}>{router}</React.Suspense>
      </I18nShell>
    );
  }
}

ShellWrapper.propTypes = {
  accessToken: PropTypes.string,
  basePath: PropTypes.string,
  children: PropTypes.element.isRequired,
  customEnvLabel: PropTypes.string,
  customSearch: PropTypes.object,
  defaultComponent: PropTypes.object,
  environment: PropTypes.string,
  helpCenter: PropTypes.object,
  hero: PropTypes.object,
  imsOrgs: PropTypes.array,
  locale: PropTypes.string,
  nps: PropTypes.object,
  onImsOrgChange: PropTypes.func,
  onSignOut: PropTypes.func,
  profile: PropTypes.object,
  selectedImsOrg: PropTypes.string,
  serviceEnvironment: PropTypes.string,
  shellResponse: PropTypes.object,
  workspaces: PropTypes.array
};

ShellWrapper.defaultProps = {
  accessToken: null,
  customEnvLabel: '',
  customSearch: {
    enabled: false,
    onClick: () => {},
    open: false
  },
  environment: 'dev',
  helpCenter: {
    featured: [],
    resources: []
  },
  hero: {
    icon: null,
    shortTitle: '',
    title: ''
  },
  imsOrgs: [],
  locale: 'en-US',
  nps: {
    enabled: false,
    sampling: 10,
    showToAdobeUsers: true
  },
  profile: {},
  selectedImsOrg: null,
  workspaces: []
};
